import { CompanyEntryDto } from "@advanced-sales-technologies/backend-client/models/CompanyEntryDto";
import { ColumnDef } from "@tanstack/react-table";
import { TFunction } from "i18next";

import { HeaderDictionary } from "@/types/Table.type";
import type { SearchParams } from "@/hooks/api/search/useInfiniteSearch";
import branchesColumn from "@/components/molecules/table/search/columns/BranchesColumn";
import companyEmailColumn from "@/components/molecules/table/search/columns/CompanyEmailColumn";
import companyNameColumn from "@/components/molecules/table/search/columns/CompanyNameColumn";
import companyPhoneColumn from "@/components/molecules/table/search/columns/CompanyPhoneColumn";
import descriptionColumn from "@/components/molecules/table/search/columns/DescriptionColumn";
import domainsColumn from "@/components/molecules/table/search/columns/DomainsColumn";
import earningsExactColumn from "@/components/molecules/table/search/columns/EarningsExactColumn";
import earningsRangeColumn from "@/components/molecules/table/search/columns/EarningsRangeColumn";
import foundingDateColumn from "@/components/molecules/table/search/columns/FoundingDateColumn";
import headcountExactColumn from "@/components/molecules/table/search/columns/HeadcountExactColumn";
import headcountRangeColumn from "@/components/molecules/table/search/columns/HeadcountRangeColumn";
import hqAddressColumn from "@/components/molecules/table/search/columns/HQAddressColumn";
import hqCityColumn from "@/components/molecules/table/search/columns/HQCityColumn";
import hqCountryColumn from "@/components/molecules/table/search/columns/HQCountryColumn";
import hqEmailColumn from "@/components/molecules/table/search/columns/HQEmailColumn";
import hqFullAddressColumn from "@/components/molecules/table/search/columns/HQFullAddressColumn";
import hqLine2Column from "@/components/molecules/table/search/columns/HQLine2Column";
import hqPhoneColumn from "@/components/molecules/table/search/columns/HQPhoneColumn";
import hqStateColumn from "@/components/molecules/table/search/columns/HQStateColumn";
import hqStreetColumn from "@/components/molecules/table/search/columns/HQStreetColumn";
import hqZipColumn from "@/components/molecules/table/search/columns/HQZipColumn";
import industryColumn from "@/components/molecules/table/search/columns/IndustryColumn";
import legalFormColumn from "@/components/molecules/table/search/columns/LegalFormColumn";
import linkedInCompanyColumn from "@/components/molecules/table/search/columns/LinkedInCompanyColumn";
import nameAliasesColumn from "@/components/molecules/table/search/columns/NameAliasesColumn";
import officeAddressColumn from "@/components/molecules/table/search/columns/OfficeAddressColumn";
import officeCityColumn from "@/components/molecules/table/search/columns/OfficeCityColumn";
import officeCountryColumn from "@/components/molecules/table/search/columns/OfficeCountryColumn";
import officeEmailColumn from "@/components/molecules/table/search/columns/OfficeEmailColumn";
import officeFullAddressColumn from "@/components/molecules/table/search/columns/OfficeFullAddressColumn";
import officeLine2Column from "@/components/molecules/table/search/columns/OfficeLine2Column";
import officePhoneColumn from "@/components/molecules/table/search/columns/OfficePhoneColumn";
import officeStateColumn from "@/components/molecules/table/search/columns/OfficeStateColumn";
import officeStreetColumn from "@/components/molecules/table/search/columns/OfficeStreetColumn";
import officeZipColumn from "@/components/molecules/table/search/columns/OfficeZipColumn";
import personEmailColumn from "@/components/molecules/table/search/columns/PersonEmailColumn";
import personFirstNameColumn from "@/components/molecules/table/search/columns/PersonFirstNameColumn";
import personLastNameColumn from "@/components/molecules/table/search/columns/PersonLastNameColumn";
import personLinkedInColumn from "@/components/molecules/table/search/columns/PersonLinkedInColumn";
import personNameColumn from "@/components/molecules/table/search/columns/PersonNameColumn";
import personRoleColumn, {
  ContactPersonColumnProps,
} from "@/components/molecules/table/search/columns/PersonRoleColumn";
import revenueExactColumn from "@/components/molecules/table/search/columns/RevenueExactColumn";
import revenueRangeColumn from "@/components/molecules/table/search/columns/RevenueRangeColumn";
import statusColumn from "@/components/molecules/table/search/columns/StatusColumn";
import technologiesColumn from "@/components/molecules/table/search/columns/TechnologiesColumn";
import vatIdColumn from "@/components/molecules/table/search/columns/VatIdColumn";
import websiteColumn from "@/components/molecules/table/search/columns/WebsiteColumn";

export enum CompanyListGroups {
  POINT_OF_CONTACT = "point_of_contact",
  OFFICE = "office",
  HQ = "hq",
}

export enum CompanyListColumns {
  COMPANY_NAME = "company_name",
  INDUSTRIES = "industries",
  WEBSITE = "website",
  DOMAINS = "domains",
  POINT_OF_CONTACT_POSITION = "point_of_contact_position",
  POINT_OF_CONTACT_FULL_NAME = "point_of_contact_full_name",
  POINT_OF_CONTACT_FIRST_NAME = "point_of_contact_first_name",
  POINT_OF_CONTACT_LAST_NAME = "point_of_contact_last_name",
  POINT_OF_CONTACT_EMAIL = "point_of_contact_email",
  POINT_OF_CONTACT_PHONE = "point_of_contact_phone",
  POINT_OF_CONTACT_LINKEDIN = "point_of_contact_linkedin",
  CONTACT_INFO_EMAIL = "contact_info_email",
  CONTACT_INFO_PHONE = "contact_info_phone",
  OFFICE_ADDRESS = "office_address",
  OFFICE_FULL_ADDRESS = "office_full_address",
  OFFICE_EMAIL = "office_email",
  OFFICE_PHONE = "office_phone",
  OFFICE_STREET = "office_street",
  OFFICE_LINE2 = "office_line2",
  OFFICE_ZIP_CODE = "office_zip_code",
  OFFICE_CITY = "office_city",
  OFFICE_COUNTRY = "office_country",
  OFFICE_STATE = "office_state",
  DESCRIPTION = "description",
  HEADCOUNT_RANGE = "headcount_range",
  REVENUE_RANGE = "revenue_range",
  EARNINGS_RANGE = "earnings_range",
  TOOLKIT = "toolkit",
  LINKEDIN_URL = "linkedin_url",

  OFFICE_HQ_LOCATION = "office_hq_location",
  OFFICE_HQ_FULL_ADDRESS = "office_hq_full_address",
  OFFICE_HQ_EMAIL = "office_hq_email",
  OFFICE_HQ_PHONE = "office_hq_phone",
  OFFICE_HQ_STREET = "office_hq_street",
  OFFICE_HQ_LINE2 = "office_hq_line2",
  OFFICE_HQ_ZIP_CODE = "office_hq_zip_code",
  OFFICE_HQ_CITY = "office_hq_city",
  OFFICE_HQ_COUNTRY = "office_hq_country",
  OFFICE_HQ_STATE = "office_hq_state",
  NUMBER_OF_OFFICES = "number_of_offices",
  HEADCOUNT_EXACT = "headcount_exact",
  EARNINGS_EXACT = "earning_exact",
  REVENUE_EXACT = "revenue_exact",
  FOUNDING_DATE = "founding_date",
  NAME_ALIASES = "name_aliases",
  LEGAL_FORM = "legal_form",
  VAT_ID = "vat_id",
  STATUS = "status",
}

export const officeAddressColumns: CompanyListColumns[] = [
  CompanyListColumns.OFFICE_FULL_ADDRESS,
  CompanyListColumns.OFFICE_CITY,
  CompanyListColumns.OFFICE_COUNTRY,
  CompanyListColumns.OFFICE_STATE,
  CompanyListColumns.OFFICE_STREET,
  CompanyListColumns.OFFICE_LINE2,
  CompanyListColumns.OFFICE_ZIP_CODE,
];

export const officeHqAddressColumns: CompanyListColumns[] = [
  CompanyListColumns.OFFICE_HQ_FULL_ADDRESS,
  CompanyListColumns.OFFICE_HQ_CITY,
  CompanyListColumns.OFFICE_HQ_COUNTRY,
  CompanyListColumns.OFFICE_HQ_STATE,
  CompanyListColumns.OFFICE_HQ_STREET,
  CompanyListColumns.OFFICE_HQ_LINE2,
  CompanyListColumns.OFFICE_HQ_ZIP_CODE,
];

export const searchColumns = (t: TFunction): HeaderDictionary => ({
  company_name: {
    id: CompanyListColumns.COMPANY_NAME,
    name: t("search.table.info.name"),
  },
  description: {
    id: CompanyListColumns.DESCRIPTION,
    name: t("search.table.info.description"),
  },
  contact_info_email: {
    id: CompanyListColumns.CONTACT_INFO_EMAIL,
    name: t("search.table.info.contact_info.email"),
  },
  contact_info_phone: {
    id: CompanyListColumns.CONTACT_INFO_PHONE,
    name: t("search.table.info.contact_info.phone"),
  },
  websites: {
    id: CompanyListColumns.WEBSITE,
    name: t("search.table.info.website"),
  },
  domains: {
    id: CompanyListColumns.DOMAINS,
    name: t("search.table.info.domains"),
  },
  linkedin: {
    id: CompanyListColumns.LINKEDIN_URL,
    name: t("search.table.socials.linkedin.url"),
  },
  industries: {
    id: CompanyListColumns.INDUSTRIES,
    name: t("search.table.info.industries"),
  },
  point_of_contact_role: {
    id: CompanyListColumns.POINT_OF_CONTACT_POSITION,
    name: t("search.table.point_of_contact.roles"),
    isMainColumn: true,
    group: CompanyListGroups.POINT_OF_CONTACT,
  },
  point_of_contact_name: {
    id: CompanyListColumns.POINT_OF_CONTACT_FULL_NAME,
    name: t("search.table.point_of_contact.full_name"),
    group: CompanyListGroups.POINT_OF_CONTACT,
  },
  point_of_contact_fname: {
    id: CompanyListColumns.POINT_OF_CONTACT_FIRST_NAME,
    name: t("search.table.point_of_contact.first_name"),
    group: CompanyListGroups.POINT_OF_CONTACT,
  },
  point_of_contact_lname: {
    id: CompanyListColumns.POINT_OF_CONTACT_LAST_NAME,
    name: t("search.table.point_of_contact.last_name"),
    group: CompanyListGroups.POINT_OF_CONTACT,
  },
  /*point_of_contact_phone: {
    id: CompanyListColumns.POINT_OF_CONTACT_PHONE,
    name: t("search.table.point_of_contact.contact_info.phone"),
    group: CompanyListGroups.POINT_OF_CONTACT,
  },*/
  point_of_contact_email: {
    id: CompanyListColumns.POINT_OF_CONTACT_EMAIL,
    name: t("search.table.point_of_contact.contact_info.email"),
    group: CompanyListGroups.POINT_OF_CONTACT,
  },
  point_of_contact_linkedin: {
    id: CompanyListColumns.POINT_OF_CONTACT_LINKEDIN,
    name: t("search.table.point_of_contact.contact_info.linkedin"),
    group: CompanyListGroups.POINT_OF_CONTACT,
  },
  founding_date: {
    id: CompanyListColumns.FOUNDING_DATE,
    name: t("search.table.info.founding_date"),
  },
  status: {
    id: CompanyListColumns.STATUS,
    name: t("search.table.info.status"),
  },
  toolkit: {
    id: CompanyListColumns.TOOLKIT,
    name: t("search.table.toolkit"),
  },
  headcount_exact: {
    id: CompanyListColumns.HEADCOUNT_EXACT,
    name: t("search.table.info.headcount.exact"),
  },
  headcount_range: {
    id: CompanyListColumns.HEADCOUNT_RANGE,
    name: t("search.table.info.headcount.range.main"),
  },
  revenue_exact: {
    id: CompanyListColumns.REVENUE_EXACT,
    name: t("search.table.finances.revenue.exact"),
  },
  revenue_range: {
    id: CompanyListColumns.REVENUE_RANGE,
    name: t("search.table.finances.revenue.range.main"),
  },
  earnings_exact: {
    id: CompanyListColumns.EARNINGS_EXACT,
    name: t("search.table.finances.earnings.exact"),
  },
  earnings_range: {
    id: CompanyListColumns.EARNINGS_RANGE,
    name: t("search.table.finances.earnings.range.main"),
  },
  office_address: {
    id: CompanyListColumns.OFFICE_ADDRESS,
    name: t("search.table.office.address.location"),
    isMainColumn: true,
    group: CompanyListGroups.OFFICE,
  },
  office_full_address: {
    id: CompanyListColumns.OFFICE_FULL_ADDRESS,
    name: t("search.table.office.address.full"),
    group: CompanyListGroups.OFFICE,
  },
  office_address_street: {
    id: CompanyListColumns.OFFICE_STREET,
    name: t("search.table.office.address.street"),
    group: CompanyListGroups.OFFICE,
  },
  office_address_line2: {
    id: CompanyListColumns.OFFICE_LINE2,
    name: t("search.table.office.address.line2"),
    group: CompanyListGroups.OFFICE,
  },
  office_address_zip: {
    id: CompanyListColumns.OFFICE_ZIP_CODE,
    name: t("search.table.office.address.zipcode"),
    group: CompanyListGroups.OFFICE,
  },
  office_address_city: {
    id: CompanyListColumns.OFFICE_CITY,
    name: t("search.table.office.address.city"),
    group: CompanyListGroups.OFFICE,
  },
  office_address_country: {
    id: CompanyListColumns.OFFICE_COUNTRY,
    name: t("search.table.office.address.country_code"),
    group: CompanyListGroups.OFFICE,
  },
  office_address_state: {
    id: CompanyListColumns.OFFICE_STATE,
    name: t("search.table.office.address.state"),
    group: CompanyListGroups.OFFICE,
  },
  office_contact_info_phone: {
    id: CompanyListColumns.OFFICE_PHONE,
    name: t("search.table.office.contact_info.phone"),
    group: CompanyListGroups.OFFICE,
  },
  office_contact_info_email: {
    id: CompanyListColumns.OFFICE_EMAIL,
    name: t("search.table.office.contact_info.email"),
    group: CompanyListGroups.OFFICE,
  },
  hq: {
    id: CompanyListColumns.OFFICE_HQ_LOCATION,
    name: t("search.table.office_hq.address.location"),
    isMainColumn: true,
    group: CompanyListGroups.HQ,
  },
  hq_full_address: {
    id: CompanyListColumns.OFFICE_HQ_FULL_ADDRESS,
    name: t("search.table.office_hq.address.full"),
    group: CompanyListGroups.HQ,
  },
  hq_street: {
    id: CompanyListColumns.OFFICE_HQ_STREET,
    name: t("search.table.office_hq.address.street"),
    group: CompanyListGroups.HQ,
  },
  hq_line2: {
    id: CompanyListColumns.OFFICE_HQ_LINE2,
    name: t("search.table.office_hq.address.line2"),
    group: CompanyListGroups.HQ,
  },
  hq_zip: {
    id: CompanyListColumns.OFFICE_HQ_ZIP_CODE,
    name: t("search.table.office_hq.address.zipcode"),
    group: CompanyListGroups.HQ,
  },
  hq_city: {
    id: CompanyListColumns.OFFICE_HQ_CITY,
    name: t("search.table.office_hq.address.city"),
    group: CompanyListGroups.HQ,
  },
  hq_country: {
    id: CompanyListColumns.OFFICE_HQ_COUNTRY,
    name: t("search.table.office_hq.address.country_code"),
    group: CompanyListGroups.HQ,
  },
  hq_state: {
    id: CompanyListColumns.OFFICE_HQ_STATE,
    name: t("search.table.office_hq.address.state"),
    group: CompanyListGroups.HQ,
  },
  hq_email: {
    id: CompanyListColumns.OFFICE_HQ_EMAIL,
    name: t("search.table.office_hq.contact_info.email"),
    group: CompanyListGroups.HQ,
  },
  hq_phone: {
    id: CompanyListColumns.OFFICE_HQ_PHONE,
    name: t("search.table.office_hq.contact_info.phone"),
    group: CompanyListGroups.HQ,
  },
  number_of_offices: {
    id: CompanyListColumns.NUMBER_OF_OFFICES,
    name: t("search.table.number_of_branches"),
  },
  vat_id: {
    id: CompanyListColumns.VAT_ID,
    name: t("search.table.info.vat_id"),
  },
  legal_form: {
    id: CompanyListColumns.LEGAL_FORM,
    name: t("search.table.info.legal_form"),
  },
  name_aliases: {
    id: CompanyListColumns.NAME_ALIASES,
    name: t("search.table.info.name_aliases"),
  },
});

export const flatMapping = {
  "info.name": CompanyListColumns.COMPANY_NAME,
  "info.description": CompanyListColumns.DESCRIPTION,
  "info.industries": CompanyListColumns.INDUSTRIES,
  "info.website": CompanyListColumns.WEBSITE,
  "info.domains": CompanyListColumns.DOMAINS,
  "info.founding_date": CompanyListColumns.FOUNDING_DATE,
  "info.status": CompanyListColumns.STATUS,
  "info.headcount.range.from": CompanyListColumns.HEADCOUNT_RANGE,
  "info.headcount.range.to": CompanyListColumns.HEADCOUNT_RANGE,
  "info.headcount.exact": CompanyListColumns.HEADCOUNT_EXACT,
  "info.name_aliases": CompanyListColumns.NAME_ALIASES,
  "info.legal_form": CompanyListColumns.LEGAL_FORM,
  "info.vat_id": CompanyListColumns.VAT_ID,
  "info.contact_info.phone": CompanyListColumns.CONTACT_INFO_PHONE,
  "info.contact_info.email": CompanyListColumns.CONTACT_INFO_EMAIL,

  "office.address.full": CompanyListColumns.OFFICE_FULL_ADDRESS,
  "office.address.city": CompanyListColumns.OFFICE_CITY,
  "office.address.country_code": CompanyListColumns.OFFICE_COUNTRY,
  "office.address.line2": CompanyListColumns.OFFICE_LINE2,
  "office.address.state": CompanyListColumns.OFFICE_STATE,
  "office.address.street": CompanyListColumns.OFFICE_STREET,
  "office.address.zipcode": CompanyListColumns.OFFICE_ZIP_CODE,
  "office.contact_info.phone": CompanyListColumns.OFFICE_PHONE,
  "office.contact_info.email": CompanyListColumns.OFFICE_EMAIL,

  number_of_branches: CompanyListColumns.NUMBER_OF_OFFICES,

  "finances.revenue.range.from": CompanyListColumns.REVENUE_RANGE,
  "finances.revenue.range.to": CompanyListColumns.REVENUE_RANGE,
  "finances.revenue.exact": CompanyListColumns.REVENUE_EXACT,

  "finances.earnings.range.from": CompanyListColumns.EARNINGS_RANGE,
  "finances.earnings.range.to": CompanyListColumns.EARNINGS_RANGE,
  "finances.earnings.exact": CompanyListColumns.EARNINGS_EXACT,

  toolkit: CompanyListColumns.TOOLKIT,

  "point_of_contact.name": CompanyListColumns.POINT_OF_CONTACT_FULL_NAME,
  "point_of_contact.first_name": CompanyListColumns.POINT_OF_CONTACT_FIRST_NAME,
  "point_of_contact.last_name": CompanyListColumns.POINT_OF_CONTACT_LAST_NAME,
  "point_of_contact.roles": CompanyListColumns.POINT_OF_CONTACT_POSITION,
  "point_of_contact.phone": CompanyListColumns.POINT_OF_CONTACT_PHONE,
  "point_of_contact.email": CompanyListColumns.POINT_OF_CONTACT_EMAIL,
  "point_of_contact.linkedin": CompanyListColumns.POINT_OF_CONTACT_LINKEDIN,

  "office_hq.address.full": CompanyListColumns.OFFICE_HQ_FULL_ADDRESS,
  "office_hq.address.city": CompanyListColumns.OFFICE_HQ_CITY,
  "office_hq.address.country_code": CompanyListColumns.OFFICE_HQ_COUNTRY,
  "office_hq.address.line2": CompanyListColumns.OFFICE_HQ_LINE2,
  "office_hq.address.state": CompanyListColumns.OFFICE_HQ_STATE,
  "office_hq.address.street": CompanyListColumns.OFFICE_HQ_STREET,
  "office_hq.address.zipcode": CompanyListColumns.OFFICE_HQ_ZIP_CODE,
  "office_hq.contact_info.phone": CompanyListColumns.OFFICE_HQ_PHONE,
  "office_hq.contact_info.email": CompanyListColumns.OFFICE_HQ_EMAIL,

  "socials.linkedin.url": CompanyListColumns.LINKEDIN_URL,
};

export type SearchColumnsProps = {
  t: TFunction;
  listId?: string | null;
  searchParams?: SearchParams;
  queryKey: readonly [string, ...unknown[]];
} & ContactPersonColumnProps;

export const searchColumnsDef = ({
  t,
  listId,
  searchParams,
  queryKey,
  onShowPersonColumns,
}: SearchColumnsProps): ColumnDef<CompanyEntryDto>[] => [
  companyNameColumn({ t, listId, searchParams, queryKey }),
  industryColumn(t),
  websiteColumn({ t, listId, searchParams, queryKey }),

  personRoleColumn({ t, listId, searchParams, queryKey, onShowPersonColumns }),
  personNameColumn(t),
  personEmailColumn({ t, listId, searchParams, queryKey }),
  personLinkedInColumn({ t, listId, searchParams, queryKey }),
  personFirstNameColumn(t),
  personLastNameColumn(t),

  companyEmailColumn({ t, listId, searchParams, queryKey }),
  companyPhoneColumn({ t, listId, searchParams, queryKey }),

  officeAddressColumn({ t, listId, searchParams, queryKey }),
  officeFullAddressColumn(t),
  officeEmailColumn({ t, listId, searchParams, queryKey }),
  officePhoneColumn({ t, listId, searchParams, queryKey }),
  officeStreetColumn(t),
  officeLine2Column(t),
  officeZipColumn(t),
  officeCityColumn(t),
  officeCountryColumn(t),
  officeStateColumn(t),

  descriptionColumn(t),

  headcountExactColumn(t),
  headcountRangeColumn(t),
  revenueExactColumn(t),
  revenueRangeColumn(t),
  earningsExactColumn(t),
  earningsRangeColumn(t),

  technologiesColumn(t),
  domainsColumn(t),
  foundingDateColumn(t),
  statusColumn(t),

  hqAddressColumn(t),
  hqFullAddressColumn(t),
  hqStreetColumn(t),
  hqLine2Column(t),
  hqZipColumn(t),
  hqCityColumn(t),
  hqCountryColumn(t),
  hqStateColumn(t),
  hqEmailColumn({ t, listId, searchParams, queryKey }),
  hqPhoneColumn({ t, listId, searchParams, queryKey }),

  branchesColumn(t),
  vatIdColumn(t),
  legalFormColumn(t),
  nameAliasesColumn(t),
  linkedInCompanyColumn({ t, listId, searchParams, queryKey }),
];
